import { Helmet } from "react-helmet-async";

/**
 * Canonical production origin used for self-referencing canonicals.
 * Kept as a stable constant so canonicals never point at the preview host.
 * If the app is ever cloned to a new domain, change this one string.
 */
const CANONICAL_ORIGIN = "https://ohioaccidentaid.com";

function _canonicalFor(pathnameOverride) {
  if (typeof window === "undefined") return undefined;
  const path = (pathnameOverride || window.location.pathname || "/").replace(/\/+$/, "") || "/";
  // Strip any query string / hash from canonicals (they are noise for indexing).
  return `${CANONICAL_ORIGIN}${path === "/" ? "" : path}` || CANONICAL_ORIGIN;
}

export default function SEO({
  title = "Ohio Accident Aid | Free 24/7 Help for Accident Victims",
  description = "Injured in Ohio? Get a free, no-obligation case review with experienced local attorneys. Available 24/7. No fees unless you win.",
  image,
  imageAlt,
  url,                 // Explicit canonical URL, overrides derived pathname.
  pathname,            // Explicit pathname, canonical becomes CANONICAL_ORIGIN + pathname.
  type = "website",
  schema,
  noindex = false,     // Set true on admin/auth/private routes.
  extraSchemas = [],   // Additional JSON-LD blocks (BreadcrumbList, Organization, etc.).
}) {
  const fullTitle = title.includes("Ohio Accident Aid") ? title : `${title} | Ohio Accident Aid`;
  // Do not emit a self-canonical on noindex pages (soft-404 branches, admin,
  // etc.). A canonical to a URL that itself won't be indexed is misleading
  // to crawlers. When noindex=true the canonical is intentionally omitted.
  const canonical = noindex ? null : (url || _canonicalFor(pathname));
  const robots = noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1";
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Ohio Accident Aid" />
      {canonical && <meta property="og:url" content={canonical} />}
      {image && <meta property="og:image" content={image} />}
      {image && imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
      {image && imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
      {Array.isArray(extraSchemas) && extraSchemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
